import { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { useNavigate } from 'react-router-dom';
import SubscriptionPlanService from 'services/subscriptionPlan.api';

export default function CreateSubscriptionPlan() {
  const navigate = useNavigate();
  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    period: 'month' as 'month' | 'year',
    priceIls: '',
    priceUsd: '',
    priceEur: '',
    priceGbp: '',
    grade: '',
    freeTrialDays: '',
    order: '',
    design: '',
    visible: true,
    isMostPopular: false,
  });

  const handleChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = useCallback(async () => {
    if (!form.name) {
      toast({
        title: 'Validation',
        description: 'Plan name is required',
        status: 'warning',
        duration: 3000,
      });
      return;
    }
    setLoading(true);
    try {
      const price = {
        ils: Number(form.priceIls) || 0,
        usd: Number(form.priceUsd) || 0,
        eur: Number(form.priceEur) || 0,
        gbp: Number(form.priceGbp) || 0,
      };
      await SubscriptionPlanService.createSubscriptionPlan({
        name: form.name,
        description: form.description,
        period: form.period,
        price,
        grade: form.grade ? Number(form.grade) : undefined,
        freeTrialDays: form.freeTrialDays
          ? Number(form.freeTrialDays)
          : undefined,
        order: form.order ? Number(form.order) : undefined,
        design: form.design || undefined,
        visible: form.visible,
        isMostPopular: form.isMostPopular,
      });
      toast({
        title: 'Success',
        description: 'Subscription plan created',
        status: 'success',
        duration: 3000,
      });
      navigate('/admin/subscription_plans/overview');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create plan',
        status: 'error',
        duration: 3000,
      });
    }
    setLoading(false);
  }, [form, navigate, toast]);

  return (
    <Flex
      direction="column"
      minH="100vh"
      align="center"
      pt={{ sm: '125px', lg: '75px' }}
      position="relative"
    >
      <Box
        h="45vh"
        bgGradient="linear(to-b, brand.400, brand.600)"
        position="absolute"
        w="100%"
        borderRadius="20px"
      />
      <Tabs
        variant="unstyled"
        zIndex="0"
        mt={{ base: '60px', md: '165px' }}
        display="flex"
        flexDirection="column"
      >
        <TabPanels mt="24px" maxW={{ md: '90%', lg: '100%' }} mx="auto">
          <TabPanel
            w={{ sm: '330px', md: '700px', lg: '850px' }}
            p="0px"
            mx="auto"
          >
            <Card p="30px">
              <Text
                color={textColor}
                fontSize="2xl"
                fontWeight="700"
                mb="20px"
              >
                Create Subscription Plan
              </Text>
              <Stack direction="column" spacing="20px">
                <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px">
                  <FormControl>
                    <FormLabel fontSize="sm" color={textColor} fontWeight="600">
                      Plan Name *
                    </FormLabel>
                    <Input
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="e.g. Premium"
                      size="md"
                      borderRadius="8px"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm" color={textColor} fontWeight="600">
                      Period
                    </FormLabel>
                    <Select
                      name="period"
                      value={form.period}
                      onChange={handleChange}
                      size="md"
                      borderRadius="8px"
                    >
                      <option value="month">Month</option>
                      <option value="year">Year</option>
                    </Select>
                  </FormControl>
                </SimpleGrid>

                <FormControl>
                  <FormLabel fontSize="sm" color={textColor} fontWeight="600">
                    Description
                  </FormLabel>
                  <Input
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    placeholder="Plan description"
                    size="md"
                    borderRadius="8px"
                  />
                </FormControl>

                <Text color={textColor} fontSize="lg" fontWeight="600">
                  Pricing
                </Text>
                <SimpleGrid columns={{ base: 2, md: 4 }} gap="20px">
                  <FormControl>
                    <FormLabel fontSize="sm" color={textColor}>
                      ILS
                    </FormLabel>
                    <Input
                      name="priceIls"
                      type="number"
                      value={form.priceIls}
                      onChange={handleChange}
                      size="md"
                      borderRadius="8px"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm" color={textColor}>
                      USD
                    </FormLabel>
                    <Input
                      name="priceUsd"
                      type="number"
                      value={form.priceUsd}
                      onChange={handleChange}
                      size="md"
                      borderRadius="8px"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm" color={textColor}>
                      EUR
                    </FormLabel>
                    <Input
                      name="priceEur"
                      type="number"
                      value={form.priceEur}
                      onChange={handleChange}
                      size="md"
                      borderRadius="8px"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm" color={textColor}>
                      GBP
                    </FormLabel>
                    <Input
                      name="priceGbp"
                      type="number"
                      value={form.priceGbp}
                      onChange={handleChange}
                      size="md"
                      borderRadius="8px"
                    />
                  </FormControl>
                </SimpleGrid>

                <Text color={textColor} fontSize="lg" fontWeight="600">
                  Settings
                </Text>
                <SimpleGrid columns={{ base: 1, md: 3 }} gap="20px">
                  <FormControl>
                    <FormLabel fontSize="sm" color={textColor}>
                      Grade
                    </FormLabel>
                    <Input
                      name="grade"
                      type="number"
                      value={form.grade}
                      onChange={handleChange}
                      size="md"
                      borderRadius="8px"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm" color={textColor}>
                      Free Trial Days
                    </FormLabel>
                    <Input
                      name="freeTrialDays"
                      type="number"
                      value={form.freeTrialDays}
                      onChange={handleChange}
                      size="md"
                      borderRadius="8px"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm" color={textColor}>
                      Order
                    </FormLabel>
                    <Input
                      name="order"
                      type="number"
                      value={form.order}
                      onChange={handleChange}
                      size="md"
                      borderRadius="8px"
                    />
                  </FormControl>
                </SimpleGrid>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px">
                  <FormControl>
                    <FormLabel fontSize="sm" color={textColor}>
                      Design
                    </FormLabel>
                    <Input
                      name="design"
                      value={form.design}
                      onChange={handleChange}
                      size="md"
                      borderRadius="8px"
                    />
                  </FormControl>
                  <Flex gap="30px" align="center" pt="25px">
                    <FormControl display="flex" alignItems="center" w="auto">
                      <Switch
                        name="visible"
                        isChecked={form.visible}
                        onChange={handleChange}
                        colorScheme="brand"
                      />
                      <FormLabel mb="0" ml="10px" fontSize="sm">
                        Visible
                      </FormLabel>
                    </FormControl>
                    <FormControl display="flex" alignItems="center" w="auto">
                      <Switch
                        name="isMostPopular"
                        isChecked={form.isMostPopular}
                        onChange={handleChange}
                        colorScheme="brand"
                      />
                      <FormLabel mb="0" ml="10px" fontSize="sm">
                        Most Popular
                      </FormLabel>
                    </FormControl>
                  </Flex>
                </SimpleGrid>
              </Stack>

              <Flex justify="flex-end" mt="24px" gap="12px">
                <Button
                  variant="outline"
                  borderRadius="16px"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>
                <Button
                  variant="darkBrand"
                  borderRadius="16px"
                  isLoading={loading}
                  onClick={handleSubmit}
                >
                  Create Plan
                </Button>
              </Flex>
            </Card>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Flex>
  );
}
